import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Baby, GraduationCap, Palette, Volleyball, ShieldCheck, BookOpen } from 'lucide-react';
import './ProgramsPage.css';
import { programsPublicApi } from '../../api/adminApi';
import { API_BASE } from '../../api/axiosInstance';
import gridone from '../../assets/images/gridone.svg';
import gridtwo from '../../assets/images/gridtwo.svg';
import gridthree from '../../assets/images/gridthree.svg';
import gridfour from '../../assets/images/gridfour.svg';
import bluecalendor from '../../assets/images/bluecalendar.svg';
const FALLBACK_IMAGES = [gridone, gridtwo, gridthree, gridfour];

/** S3 URLs may include spaces; encode for valid img src. */
function safeMediaUrl(url) {
  if (!url || typeof url !== 'string') return '';
  const t = url.trim();
  if (!t) return '';
  try {
    return /\s/.test(t) ? encodeURI(t) : t;
  } catch {
    return t;
  }
}

function resolveImageUrl(url, idx) {
  if (url && typeof url === 'string') {
    const u = url.trim();
    if (!u) return FALLBACK_IMAGES[idx % FALLBACK_IMAGES.length] ?? gridone;
    return u.startsWith('http') ? u : `${API_BASE}${u.startsWith('/') ? '' : '/'}${u}`;
  }
  return FALLBACK_IMAGES[idx % FALLBACK_IMAGES.length] ?? gridone;
}

function mapApiProgramToCard(p, idx) {
  const min = p.age_min ?? p.ageMin;
  const max = p.age_max ?? p.ageMax;
  let ageGroup = '—';
  if (min != null && max != null && max > 0) ageGroup = `${min}–${max} years`;
  else if (min != null) ageGroup = `Age ${min}`;

  const features = [];
  if (p.feature_timeline) features.push({ icon: 'clock', text: p.feature_timeline });
  if (p.feature_ratio) features.push({ icon: 'ratio', text: p.feature_ratio });
  if (features.length === 0) features.push({ icon: 'clock', text: 'Flexible scheduling' }, { icon: 'ratio', text: 'Small class sizes' });

  const title = p.name || p.title || p.programName || 'Program';
  const photoSafe = safeMediaUrl(p.photo_url ?? p.photoUrl);
  const iconSafe = safeMediaUrl(p.icon_url ?? p.iconUrl);
  const heroUrl = photoSafe || iconSafe;

  return {
    id: p.id,
    title,
    ageGroup,
    description: p.description || p.desc || '',
    features,
    image: resolveImageUrl(heroUrl, idx),
    imageAlt: title,
    /** Upload icon for the yellow circle; empty → Lucide fallback */
    iconImageUrl: iconSafe ? resolveImageUrl(iconSafe, idx) : '',
    layout: idx % 2 === 0 ? 'image-left' : 'image-right',
  };
}

function getProgramCardIcon(program) {
  const t = [program?.title, program?.description, ...(program?.features || []).map((f) => f?.text)]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();

  // After-school: games / sports / play
  if (
    t.includes('afterschool') ||
    t.includes('after school') ||
    t.includes('after-school') ||
    t.includes('homework') ||
    t.includes('enrichment') ||
    t.includes('enriching')
  ) {
    return <Volleyball size={30} strokeWidth={2} />;
  }

  // Pre-K / kindergarten readiness
  if (t.includes('pre-k') || t.includes('prek') || t.includes('kindergarten') || t.includes('readiness')) {
    return <GraduationCap size={30} strokeWidth={2} />;
  }

  // Younger ages (2K/3K, toddlers, etc.)
  if (t.includes('2k') || t.includes('3k') || t.includes('toddler') || t.includes('age 2') || t.includes('age 3')) {
    return <Baby size={30} strokeWidth={2} />;
  }

  // Creativity / arts
  if (
    t.includes('arts') ||
    t.includes('music') ||
    t.includes('creative') ||
    t.includes('creativity') ||
    t.includes('paint') ||
    t.includes('drawing') ||
    t.includes('craft') ||
    t.includes('palette')
  ) {
    return <Palette size={30} strokeWidth={2} />;
  }

  // Safety / secure
  if (t.includes('safe') || t.includes('safety') || t.includes('secure') || t.includes('security')) {
    return <ShieldCheck size={30} strokeWidth={2} />;
  }

  // Literacy / general learning fallback
  if (t.includes('literacy') || t.includes('reading') || t.includes('learn') || t.includes('learning')) {
    return <BookOpen size={30} strokeWidth={2} />;
  }

  return <BookOpen size={30} strokeWidth={2} />;
}

function ProgramsPage() {
  const [programs, setPrograms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    setLoading(true);
    setError('');
    programsPublicApi
      .list(1, 10)
      .then((res) => {
        const list = (res.list || []).map((p, idx) => mapApiProgramToCard(p, idx));
        setPrograms(list);
      })
      .catch((err) => setError(err?.userMessage || 'Failed to load programs'))
      .finally(() => setLoading(false));
  }, []);
  return (
    <main className="programs-page">
      {/* Hero Section */}
      <section className="programs-hero" aria-label="Explore our programs">
        {/* <div className="programs-hero-bg" aria-hidden="true" /> */}
        <div className="programs-hero-wave" aria-hidden="true">
          <svg viewBox="0 0 1440 80" preserveAspectRatio="none" xmlns="http://www.w3.org/2000/svg">
            <path fill="#FFD84D" d="M0 80V40c120 20 240 40 360 40s240-20 360-40 240-40 360-40 240 20 360 40v40H0z" />
          </svg>
        </div>
        <div className="programs-hero-inner">
          <h1 className="programs-hero-title">Explore Our Programs</h1>
          <p className="programs-hero-desc">
            Age-appropriate learning designed to prepare children for lifelong success. Each program is tailored to meet developmental milestones.
          </p>
        </div>
      </section>

      {/* Program Cards */}
      <section className="programs-content" aria-label="Program listings">
        <div className="programs-inner">
          {loading && (
            <div className="programs-loading" role="status" aria-live="polite">
              Loading programs…
            </div>
          )}
          {error && (
            <div className="programs-error" role="alert">
              {error}
            </div>
          )}
          {!loading && !error && programs.map((program, idx) => (
            <article
              key={program.id ?? idx}
              className={`programs-card programs-card--${program.layout}`}
              aria-labelledby={`program-title-${idx}`}
            >
              <div className="programs-card-image-wrap">
                <img
                  src={program.image}
                  alt={program.imageAlt}
                  className="programs-card-image"
                />
              </div>
              <div className="programs-card-body">
                <span className="programs-card-icon" aria-hidden="true">
                  {program.iconImageUrl ? (
                    <img src={program.iconImageUrl} alt="" className="programs-card-icon-img" />
                  ) : (
                    getProgramCardIcon(program)
                  )}
                </span>
                <h2 id={`program-title-${idx}`} className="programs-card-title">
                  {program.title}
                </h2>
                <p className="programs-card-age">{program.ageGroup}</p>
                <p className="programs-card-desc">{program.description}</p>
                <ul className="programs-card-features">
                  {program.features.map((f, i) => (
                    <li key={i}>
                      <span className="programs-card-feature-icon" aria-hidden="true">
                        {f.icon === 'clock' ? (
                          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M12 8v4l2.5 1.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                            <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="2" />
                          </svg>
                        ) : (
                          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <circle cx="9" cy="8" r="3.5" stroke="currentColor" strokeWidth="1.8" />
                            <path d="M2 20c0-3.5 3-6 7-6s7 2.5 7 6" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
                            <circle cx="17" cy="9" r="2" stroke="currentColor" strokeWidth="1.8" />
                            <path d="M21 18c0-2-1.5-3.5-3.5-3.5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
                          </svg>
                        )}
                      </span>
                      {f.text}
                    </li>
                  ))}
                </ul>
              </div>
            </article>
          ))}
        </div>
      </section>

      {/* Flexible Scheduling */}
      <section className="programs-scheduling" aria-label="Flexible scheduling options">
        <div className="programs-scheduling-inner">
          <span className="programs-scheduling-icon" aria-hidden="true">
            <img src={bluecalendor} alt="schedule" />
          </span>
          <h2 className="programs-scheduling-title">Flexible Scheduling</h2>
          <p className="programs-scheduling-desc">
            We offer flexible scheduling options to meet your family&apos;s needs. Our programs run Monday through Friday with extended day options available.
          </p>
          <div className="programs-scheduling-cards">
            <div className="programs-scheduling-card">
              <h3 className="programs-scheduling-card-title">Half Day</h3>
              <p className="programs-scheduling-card-time">8:00 AM - 12:00 PM</p>
            </div>
            <div className="programs-scheduling-card">
              <h3 className="programs-scheduling-card-title">Full Day</h3>
              <p className="programs-scheduling-card-time">8:00 AM - 3:00 PM</p>
            </div>
            <div className="programs-scheduling-card">
              <h3 className="programs-scheduling-card-title">Extended Day</h3>
              <p className="programs-scheduling-card-time">8:00am-6:30pm</p>
            </div>
          </div>
          <Link to="/contact" className="programs-scheduling-btn">Contact us for details</Link>
        </div>
      </section>
   {/* CTA */}
   <section className="cta" id="schedule-tour">
        <div className="cta-inner">
          <h2 className="cta-title">Find the Right Program for Your Child</h2>
          <p className="cta-desc">Schedule a tour to see our programs in action and meet our teachers.</p>
          <div className="cta-actions">
            <Link to={{ pathname: '/admissions', hash: 'schedule-tour' }} className="btn btn-primary cta-primary">
              <span className="btn-icon" aria-hidden="true">
                <svg
                  width="18"
                  height="18"
                  viewBox="0 0 20 20"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <rect
                    x="3"
                    y="4"
                    width="14"
                    height="13"
                    rx="2"
                    stroke="currentColor"
                    strokeWidth="1.6"
                  />
                  <path d="M3 8H17" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
                  <path d="M8 3V6" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
                  <path d="M12 3V6" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
                  <rect x="7" y="10" width="2.2" height="2.2" rx="0.4" fill="currentColor" />
                </svg>
              </span>
              Schedule Tour
            </Link>
            <Link to="/contact" className="btn btn-outline cta-secondary">
              Inquiry Now
            </Link>
          </div>

          {/* <div className="cta-meta" aria-label="Contact and hours">
            <a className="cta-meta-item" href="tel:718-462-0776">
              <span className="cta-meta-icon" aria-hidden="true">
                <svg
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M7.5 3.5L9.2 7.2C9.4 7.7 9.3 8.3 8.9 8.7L7.6 10C9 12.7 11.3 15 14 16.4L15.3 15.1C15.7 14.7 16.3 14.6 16.8 14.8L20.5 16.5C21.1 16.8 21.3 17.4 21.1 18C20.6 19.5 19.2 20.5 17.6 20.5C10.1 20.5 3.5 13.9 3.5 6.4C3.5 4.8 4.5 3.4 6 2.9C6.6 2.7 7.2 2.9 7.5 3.5Z"
                    stroke="currentColor"
                    strokeWidth="1.6"
                    strokeLinejoin="round"
                  />
                </svg>
              </span>
              <span className="cta-meta-text">718-462-0776</span>
            </a>

            <div className="cta-meta-item">
              <span className="cta-meta-icon" aria-hidden="true">
                <svg
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M12 8V12L14.5 13.5"
                    stroke="currentColor"
                    strokeWidth="1.6"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M21 12C21 16.97 16.97 21 12 21C7.03 21 3 16.97 3 12C3 7.03 7.03 3 12 3C16.97 3 21 7.03 21 12Z"
                    stroke="currentColor"
                    strokeWidth="1.6"
                  />
                </svg>
              </span>
              <span className="cta-meta-text">Monday - Friday: 8:00 AM - 7:00 PM</span>
            </div>
          </div> */}
        </div>
      </section>

      
    </main>
  );
}

export default ProgramsPage;
